/**
 * Экран данных по услуге (п. 6.4 ТЗ).
 *
 * Здесь же выбирается компания: от неё зависит текст договора, который абонент
 * увидит на следующем шаге. Выбор не на первом экране намеренно — снимок
 * паспорта можно сделать, ещё не решив, на какое юрлицо оформляется подключение.
 *
 * Адрес подключения не подставляется из адреса регистрации автоматически — по
 * ТЗ это отдельное поле. Но кнопка «Совпадает с регистрацией» есть: чаще всего
 * адреса действительно одинаковы, и набирать их дважды на планшете мучительно.
 * Копируется при этом не строка, а привязка к справочнику (город, улица, дом,
 * квартира) — тот же адрес, что биллинг увидит в карточке договора.
 */

import { useEffect, useState } from 'react'

import { ApiError, api } from '../api/client'
import type { Application, CatalogState, Company, Tariff, TvPackage } from '../api/types'
import { AddressPicker } from '../components/AddressPicker'
import type { AddressValue } from '../components/AddressPicker'
import { Alert, Button, Field, Select, Steps } from '../components/ui'
import type { SelectGroup } from '../components/ui'
import { BarcodeScanner } from '../components/BarcodeScanner'
import { clearDraft, loadDraft, saveDraft } from '../lib/draft'
import { formatDateTime, formatPhoneInput } from '../lib/format'

const DRAFT_KEY = 'service'

/**
 * Тарифы группируются по региону и населённому пункту: в таблице «Дом 100» есть
 * в нескольких улусах, отличаясь ценой подключения, и плоский список из трёх
 * десятков одинаковых названий выбрать правильно не даёт.
 */
function groupTariffs(tariffs: Tariff[]): SelectGroup[] {
  const groups = new Map<string, SelectGroup>()

  for (const tariff of tariffs) {
    const label = [tariff.region, tariff.locality].filter(Boolean).join(' — ') || 'Тарифы'
    if (!groups.has(label)) groups.set(label, { label, options: [] })
    const parts = [tariff.price, tariff.speed].filter(Boolean).join(', ')
    groups.get(label)!.options.push({
      value: tariff.code,
      label: `${tariff.name}${parts ? ` — ${parts}` : ''}${tariff.is_hit ? ' ★' : ''}`,
    })
  }

  return [...groups.values()]
}

// Модели роутеров на продажу. Пока список фиксирован; позже вынесем в справочник.
const ROUTER_MODELS = ['Cudy WR1300', 'TP-Link Archer AX1500'] as const

interface Values {
  company_code: string
  login: string
  tariff_code: string
  terminal_serial: string
  router_model: string
  router_serial: string
  tv_package_code: string
  phone: string
  email: string
  comment: string
}

interface Props {
  application: Application
  companies: Company[]
  tariffs: Tariff[]
  tvPackages: TvPackage[]
  contractNumberAutogenerate: boolean
  loginHint: string
  catalog: CatalogState
  addressBackend: string
  onCatalogRefreshed: () => void
  onDone: (application: Application) => void
  onBack: () => void
}

export function ServiceScreen({
  application,
  companies,
  tariffs,
  tvPackages,
  contractNumberAutogenerate,
  loginHint,
  catalog,
  addressBackend,
  onCatalogRefreshed,
  onDone,
  onBack,
}: Props) {
  const draftId = `${application.id}.${DRAFT_KEY}`

  const [values, setValues] = useState<Values>(() => {
    const server: Values = {
      // Единственную компанию выбирать не из чего — подставляем сразу.
      company_code:
        application.company_code ?? (companies.length === 1 ? companies[0]!.code : ''),
      login: application.login ?? '',
      tariff_code: application.tariff_code ?? '',
      terminal_serial: application.terminal_serial ?? '',
      router_model: application.router_model ?? '',
      router_serial: application.router_serial ?? '',
      tv_package_code: application.tv_package_code ?? '',
      phone: application.phone ? formatPhoneInput(application.phone) : '',
      email: application.email ?? '',
      comment: application.comment ?? '',
    }
    const draft = loadDraft<Partial<Values>>(draftId)
    return draft ? { ...server, ...draft } : server
  })
  const [address, setAddress] = useState<AddressValue>(() => {
    const draft = loadDraft<AddressValue>(`${draftId}.address`)
    if (draft) return draft
    return {
      city_id: application.connection_city_id,
      street_id: application.connection_street_id,
      house_id: application.connection_house_id,
      flat: application.connection_flat ?? '',
      text: application.connection_address ?? '',
    }
  })
  // Прописка с прошлого шага: чаще всего абонент подключается по месту
  // регистрации, и второй раз проходить каскад незачем.
  const registration: AddressValue = {
    city_id: application.registration_city_id,
    street_id: application.registration_street_id,
    house_id: application.registration_house_id,
    flat: application.registration_flat ?? '',
    text: application.registration_address ?? '',
  }
  const [addressKey, setAddressKey] = useState(0)
  const [addressError, setAddressError] = useState<string | undefined>()
  const [contractNumber, setContractNumber] = useState(application.contract_number ?? '')
  const [errors, setErrors] = useState<Partial<Record<keyof Values | 'contract_number', string>>>({})
  const [saveError, setSaveError] = useState<ApiError | null>(null)
  const [saving, setSaving] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  const refreshCatalog = async () => {
    setRefreshing(true)
    try {
      await api.refreshCatalog()
      onCatalogRefreshed()
    } catch {
      // Причина уже видна в состоянии справочника, которое придёт следом.
      onCatalogRefreshed()
    } finally {
      setRefreshing(false)
    }
  }

  useEffect(() => {
    saveDraft(draftId, values)
  }, [draftId, values])

  useEffect(() => {
    saveDraft(`${draftId}.address`, address)
  }, [draftId, address])

  const set = (key: keyof Values) => (value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => ({ ...prev, [key]: undefined }))
  }

  // Один универсальный сканер кодов на терминал и на роутер: цель хранится в
  // scanTarget, туда же кладётся распознанный серийник.
  const [scanTarget, setScanTarget] = useState<'terminal_serial' | 'router_serial' | null>(
    null,
  )
  const [serialHint, setSerialHint] = useState<string | null>(null)
  const [routerHint, setRouterHint] = useState<string | null>(null)

  const onSerialScanned = (value: string) => {
    if (scanTarget) {
      set(scanTarget)(value)
      ;(scanTarget === 'terminal_serial' ? setSerialHint : setRouterHint)(
        'Серийный номер распознан',
      )
    }
    setScanTarget(null)
  }

  const routerSold = values.router_model.trim() !== ''

  const onAddressChange = (next: AddressValue) => {
    setAddress(next)
    setAddressError(undefined)
  }

  const copyRegistration = () => {
    onAddressChange({ ...registration })
    // Каскад сворачивается в готовый адрес только при монтировании.
    setAddressKey((key) => key + 1)
  }

  function validate(): boolean {
    const found: Partial<Record<keyof Values | 'contract_number', string>> = {}
    if (!values.company_code) found.company_code = 'Выберите компанию'
    if (!values.login.trim()) found.login = 'Обязательное поле'
    else if (/\s/.test(values.login.trim())) found.login = 'Логин без пробелов'
    if (!values.tariff_code) found.tariff_code = 'Выберите тариф'
    // Терминал выдаётся в аренду всегда — серийник нужен в соглашении и в биллинге.
    if (!values.terminal_serial.trim()) found.terminal_serial = 'Обязательное поле'
    // Серийник роутера — только когда роутер продают (он идёт в акт).
    if (routerSold && !values.router_serial.trim()) {
      found.router_serial = 'Обязательное поле'
    }

    const digits = values.phone.replace(/\D/g, '')
    if (!digits) found.phone = 'Обязательное поле'
    else if (digits.length !== 11) found.phone = 'Введите номер полностью'

    if (!values.email.trim()) found.email = 'Обязательное поле'
    else if (!/^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(values.email.trim())) {
      found.email = 'Некорректный адрес'
    }
    if (!contractNumberAutogenerate && !contractNumber.trim()) {
      found.contract_number = 'Введите номер договора'
    }
    setErrors(found)

    // Адрес — отдельным состоянием. Нужна хотя бы строка адреса: обычно это
    // выбор из справочника, но если адреса там нет — принимаем ручной ввод.
    let addressOk = true
    if (!address.text.trim()) {
      setAddressError('Укажите адрес подключения — из справочника или вручную')
      addressOk = false
    }
    return Object.keys(found).length === 0 && addressOk
  }

  const submit = async () => {
    if (!validate()) return
    setSaving(true)
    setSaveError(null)
    try {
      const updated = await api.patchApplication(application.id, {
        company_code: values.company_code,
        login: values.login.trim(),
        connection_address: address.text.trim(),
        connection_city_id: address.city_id,
        connection_street_id: address.street_id,
        connection_house_id: address.house_id,
        connection_flat: address.flat || null,
        tariff_code: values.tariff_code,
        terminal_serial: values.terminal_serial.trim() || null,
        router_model: values.router_model.trim() || null,
        // Серийник роутера имеет смысл только при продаже роутера.
        router_serial: routerSold ? values.router_serial.trim() || null : null,
        tv_package_code: values.tv_package_code || null,
        phone: values.phone,
        email: values.email.trim(),
        comment: values.comment.trim() || null,
        ...(contractNumberAutogenerate ? {} : { contract_number: contractNumber.trim() }),
      })
      clearDraft(draftId)
      clearDraft(`${draftId}.address`)
      onDone(updated)
    } catch (err) {
      setSaveError(err instanceof ApiError ? err : new ApiError(0, 'Неизвестная ошибка'))
    } finally {
      setSaving(false)
    }
  }

  const selected = tariffs.find((tariff) => tariff.code === values.tariff_code)
  const selectedTv = tvPackages.find((item) => item.code === values.tv_package_code)
  const selectedCompany = companies.find((item) => item.code === values.company_code)

  return (
    <>
      <Steps current={3} />
      <div className="card">
        <h2>Услуга и контакты</h2>
        <p className="subtitle">
          Компания, адрес подключения, тариф и контакты абонента. Почта нужна для
          отправки подписанного договора.
        </p>

        {(catalog.stale || catalog.error) && (
          <Alert
            kind="warn"
            title="Тарифы могли устареть"
            onRetry={refreshing ? undefined : refreshCatalog}
            retryLabel="Обновить тарифы"
          >
            {catalog.error ?? 'Таблицу тарифов давно не удавалось перечитать.'}{' '}
            {catalog.fetched_at
              ? `Показаны данные от ${formatDateTime(catalog.fetched_at)}.`
              : 'Показан встроенный список-пример.'}
          </Alert>
        )}

        {selectedCompany && !selectedCompany.signing_ready && (
          <Alert kind="warn" title="Договор этой компании не готов к подписанию">
            Для «{selectedCompany.name}» на сервере не собран шаблон в DocuSeal. Заполнить
            заявку можно, а подписать — нет.
          </Alert>
        )}

        {saveError && (
          <Alert
            kind="error"
            title={saveError.retryable ? 'Данные не сохранились' : 'Сервер отклонил данные'}
            onRetry={saveError.retryable ? submit : undefined}
            onDismiss={() => setSaveError(null)}
          >
            {saveError.message}
          </Alert>
        )}

        <Select
          name="company_code"
          value={values.company_code}
          onChange={set('company_code')}
          required
          error={errors.company_code}
          options={companies.map((company) => ({
            value: company.code,
            label: company.name,
          }))}
          hint={
            selectedCompany
              ? `Договор будет оформлен от имени: ${selectedCompany.full_name}`
              : 'От выбора зависит текст договора, который подпишет абонент'
          }
        />

        <Field
          name="login"
          value={values.login}
          onChange={set('login')}
          required
          error={errors.login}
          placeholder={loginHint}
          autoComplete="off"
          hint="Логин абонента в биллинге, он же номер лицевого счёта в договоре"
        />

        {addressBackend === 'stub' && (
          <Alert kind="warn" title="Адресный справочник в режиме заглушки">
            Сервер отдаёт встроенный список-пример (BGBILLING_BACKEND=stub), а не адреса из
            биллинга. Для боевого справочника нужен подключённый BGBilling.
          </Alert>
        )}

        {registration.text && registration.text !== address.text && (
          <Alert kind="ok">
            Адрес регистрации: {registration.text}
            <div className="alert-actions">
              <Button variant="secondary" onClick={copyRegistration}>
                Подключение по этому же адресу
              </Button>
            </div>
            {!registration.city_id && (
              <div className="hint">
                Он записан строкой, без привязки к справочнику — после копирования
                выберите дом, иначе оператор будет искать его руками.
              </div>
            )}
          </Alert>
        )}

        <AddressPicker
          key={`connection-${addressKey}`}
          label="Шаг 2 из 2 — адрес подключения (установка оборудования)"
          value={address}
          onChange={onAddressChange}
          error={addressError}
        />

        <Select
          name="tariff_code"
          value={values.tariff_code}
          onChange={set('tariff_code')}
          required
          error={errors.tariff_code}
          groups={groupTariffs(tariffs)}
          hint={selected?.description ?? 'Список сгруппирован по населённым пунктам из таблицы'}
        />

        {tvPackages.length > 0 && (
          <Select
            name="tv_package_code"
            label="ТВ-пакет «Смотрёшка»"
            value={values.tv_package_code}
            onChange={set('tv_package_code')}
            options={[
              { value: '', label: 'Без телевидения' },
              ...tvPackages.map((item) => ({
                value: item.code,
                label: `${item.name} — ${item.price}${item.channels ? `, ${item.channels} каналов` : ''}`,
              })),
            ]}
            hint={selectedTv?.description ?? 'Дополнительная услуга, можно не подключать'}
          />
        )}

        <div className="serial-field">
          <Field
            name="terminal_serial"
            label="Серийный номер терминала"
            value={values.terminal_serial}
            onChange={set('terminal_serial')}
            required
            error={errors.terminal_serial}
            placeholder="Отсканируйте или введите"
            hint={
              serialHint ??
              'Оптический терминал в аренду. Серийный номер попадёт в соглашение об аренде.'
            }
          />
          <Button variant="secondary" onClick={() => setScanTarget('terminal_serial')}>
            Сканировать
          </Button>
        </div>

        <Select
          name="router_model"
          label="Продажа роутера"
          value={values.router_model}
          onChange={set('router_model')}
          options={[
            { value: '', label: 'Роутер не продаётся' },
            ...ROUTER_MODELS.map((model) => ({ value: model, label: model })),
          ]}
          hint="При продаже добавится акт приёма-передачи оборудования на подпись"
        />

        {routerSold && (
          <div className="serial-field">
            <Field
              name="router_serial"
              label="Серийный номер роутера"
              value={values.router_serial}
              onChange={set('router_serial')}
              required
              error={errors.router_serial}
              placeholder="Отсканируйте или введите"
              hint={routerHint ?? 'Серийный номер проданного роутера — в акт приёма-передачи.'}
            />
            <Button variant="secondary" onClick={() => setScanTarget('router_serial')}>
              Сканировать
            </Button>
          </div>
        )}

        {scanTarget && (
          <BarcodeScanner
            title={
              scanTarget === 'terminal_serial'
                ? 'Серийный номер терминала'
                : 'Серийный номер роутера'
            }
            onPick={onSerialScanned}
            onClose={() => setScanTarget(null)}
          />
        )}

        <div className="field-row">
          <Field
            name="phone"
            type="tel"
            value={values.phone}
            onChange={(value) => set('phone')(formatPhoneInput(value))}
            required
            inputMode="tel"
            placeholder="+7 (999) 123-45-67"
            error={errors.phone}
          />
          <Field
            name="email"
            type="email"
            value={values.email}
            onChange={set('email')}
            required
            inputMode="email"
            placeholder="abonent@example.ru"
            error={errors.email}
          />
        </div>

        {!contractNumberAutogenerate && (
          <Field
            name="contract_number"
            value={contractNumber}
            onChange={(value) => {
              setContractNumber(value)
              setErrors((prev) => ({ ...prev, contract_number: undefined }))
            }}
            required
            error={errors.contract_number}
            hint="Автонумерация отключена — введите номер по журналу"
          />
        )}

        <Field
          name="comment"
          label="Примечание монтажника"
          value={values.comment}
          onChange={set('comment')}
          placeholder="Например: подъезд 2, домофон 15"
          multiline
        />

        <div className="actions">
          <Button variant="secondary" onClick={onBack} disabled={saving}>
            Назад
          </Button>
          <Button onClick={submit} busy={saving}>
            Далее
          </Button>
        </div>
      </div>
    </>
  )
}
